import { config } from 'src/data/services/common/config/config-service-factory';

export const DEFAULT_TIMEZONE = 'America/Sao_Paulo';
export const DEFAULT_LOCALE = 'pt-BR';

type DateInput = string | Date | null | undefined;

export function getConfiguredTimezone(): string {
  const configuredTimezone = config.general.timezone;

  return isValidIanaTimezone(configuredTimezone)
    ? configuredTimezone
    : DEFAULT_TIMEZONE;
}

export function getConfiguredLocale(): string {
  return config.general.language || DEFAULT_LOCALE;
}

export function formatDateTimeByTimezone(
  date: DateInput,
  timezone = getConfiguredTimezone(),
): string {
  return formatByTimezone(
    date,
    {
      dateStyle: 'short',
      timeStyle: 'short',
    },
    timezone,
  );
}

export function formatDateByTimezone(
  date: DateInput,
  timezone = getConfiguredTimezone(),
): string {
  return formatByTimezone(
    date,
    {
      dateStyle: 'short',
    },
    timezone,
  );
}

export function formatTimeByTimezone(
  date: DateInput,
  timezone = getConfiguredTimezone(),
): string {
  return formatByTimezone(
    date,
    {
      timeStyle: 'short',
    },
    timezone,
  );
}

export function formatWithTimezone(
  date: DateInput,
  options: Intl.DateTimeFormatOptions,
  timezone = getConfiguredTimezone(),
): string {
  return formatByTimezone(date, options, timezone);
}

export function getTodayDateByTimezone(
  date: Date = new Date(),
  timezone = getConfiguredTimezone(),
): string {
  const parsedDate = normalizeDate(date);

  if (!parsedDate) {
    return '';
  }

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(parsedDate);
  const year = parts.find((part) => part.type === 'year')?.value || '0000';
  const month = parts.find((part) => part.type === 'month')?.value || '00';
  const day = parts.find((part) => part.type === 'day')?.value || '00';

  return `${year}-${month}-${day}`;
}

function formatByTimezone(
  date: DateInput,
  options: Intl.DateTimeFormatOptions,
  timezone: string,
): string {
  const parsedDate = normalizeDate(date);

  if (!parsedDate) {
    return '';
  }

  return new Intl.DateTimeFormat(getConfiguredLocale(), {
    timeZone: timezone,
    ...options,
  }).format(parsedDate);
}

function normalizeDate(date: DateInput): Date | null {
  if (!date) {
    return null;
  }

  const parsedDate = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
}

function isValidIanaTimezone(timezone?: string): boolean {
  if (!timezone?.trim()) {
    return false;
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(
      new Date(),
    );
    return true;
  } catch {
    return false;
  }
}
